import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Head from 'next/head';
import Root from 'react-modal-sheet';
import { faChevronDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { usePlayerTrackInfo } from '@components/AudioPlayer/usePlayerTrackInfo';
import { ViewArtistMenuItem } from '@components/basics/Dropdowns/MenuItems/ViewArtist';
import { ViewSongMenuItem } from '@components/basics/Dropdowns/MenuItems/ViewSongMenuItem';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';
import { AudioController } from './AudioController';
import { AudioProgress } from './AudioProgress';
import { ReleaseActions } from './ReleaseActions';
import { ReleaseCover } from './ReleaseCover';
import { SongInfo } from './SongInfo';
import { Waveform } from './Waveform';

export const ExpandedPlayer = ({
  isOpen,
  closePlayer,
}: {
  isOpen: boolean;
  closePlayer: () => void;
}) => {
  const { activeTrackId } = useAudioController();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
  const [isDraggingContent, setIsDraggingContent] = React.useState(false);

  const handleContentOnTouchStart = React.useCallback(() => {
    setIsDraggingContent(true);
  }, []);

  const handleContentOnTouchEnd = React.useCallback(() => {
    setIsDraggingContent(false);
  }, []);
  const { releaseInfo } = usePlayerTrackInfo({
    trackId: activeTrackId,
    shouldFetch: true,
  });

  if (!releaseInfo) {
    return null;
  }

  return (
    <PlayerSheet
      isOpen={isOpen}
      onClose={closePlayer}
      disableDrag={isDraggingContent}
      springConfig={{ damping: 40, stiffness: 400 }}
    >
      <Head>
        <meta name="theme-color" content="#000" />
      </Head>
      <Container>
        <Content>
          <Wrapper>
            <ReleaseCover />
            <BottomContent
              // we need to disable dragging when user is interacting with the content (queue + audio slider)
              onTouchStart={handleContentOnTouchStart}
              onTouchEnd={handleContentOnTouchEnd}
            >
              <SongInfo closePlayer={closePlayer} />

              <AudioWaveformWrapper>
                <Waveform releaseId={releaseInfo?.id} />
                <AudioProgress />
              </AudioWaveformWrapper>
              <Actions>
                <AudioController />
                <ReleaseActions />
              </Actions>
            </BottomContent>
          </Wrapper>
        </Content>
        <StyledIconButton
          variant="inverted"
          icon={<FontAwesomeIcon icon={faChevronDown} />}
          onClick={closePlayer}
        />
        <DropdownWrapper>
          <ThreeDotsDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            dropdownProps={{
              wrapperContentProps: {
                collisionPadding: { right: 16 },
                css: {
                  zIndex: '$audioPlayerSheet',
                },
              },
            }}
            triggerProps={{
              variant: 'inverted',
              css: {
                backgroundColor: 'transparent',
                '&:hover, &:focus, &:active': {
                  backgroundColor: 'transparent',
                },
              },
            }}
          >
            <ItemWrapper onClick={closePlayer}>
              <ViewSongMenuItem variant="solid" href={webappUriPath(releaseInfo.webappUri)} />
            </ItemWrapper>
            <ItemWrapper onClick={closePlayer}>
              <ViewArtistMenuItem
                variant="solid"
                href={webappUriPath(releaseInfo.artist.webappUri)}
              />
            </ItemWrapper>
          </ThreeDotsDropdown>
        </DropdownWrapper>
      </Container>
    </PlayerSheet>
  );
};

const PlayerSheet = styled(Root, {
  // react-sheet zIndex is taking precedence so need to use important
  zIndex: '$audioPlayerSheet !important',
  '@tablet': {
    display: 'none',
  },
});

const Container = styled(Root.Container, {
  borderRadius: 'unset !important',
  background: '#000 !important',
  overflow: 'hidden',
  height: '100dvh !important',
});

const StyledIconButton = styled(IconButton, {
  position: 'absolute',
  top: 12,
  left: 12,
  backgroundColor: 'transparent',

  '&:hover, &:focus, &:active': {
    backgroundColor: 'transparent',
  },
});

const DropdownWrapper = styled('div', {
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: '$above1',
});

const Content = styled(Root.Content, {});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100dvh !important',
  position: 'relative',
  overflow: 'auto',
});

const BottomContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '0 16px 28px',
  zIndex: '$above1',
});

const Actions = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
});

const AudioWaveformWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  flexDirection: 'column',
  alignItems: 'top',
  marginBottom: 16,
  width: '100%',
  position: 'relative',
});

const ItemWrapper = styled('div', {});
